import { useCallback, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";

import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/src/api";

import { createAuctionAtom } from "@/src/state/createAuctionAtom";

import { useAuth } from "@/src/auth/hooks/useAuth";
import { useGetAllAuctions } from "@/src/hooks/consumerAuctions";
import { ConsumerQueryKeys } from "@/src/queries/consumer/consumerFunctions";
import { AuctionItemState, AuctionState, VehicleType } from "@/src/types/types";

import { useAtom } from "jotai";

export const useAuctionCreationOnLogin = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { isFetching: isLoadingUserAuctions, data: allAuctions } =
    useGetAllAuctions();
  const [createAuction, setCreateAuction] = useAtom(createAuctionAtom);
  const queryClient = useQueryClient();
  const [processingAuction, setProcessingAuction] = useState(false);
  const processingAuctionRef = useRef(false);

  // Check if there's ANY draft auction (user can only have ONE draft at a time)
  const anyDraftAuction = useMemo(() => {
    if (!allAuctions) return undefined;
    return allAuctions.find(
      (auction) => auction.auctionState === AuctionState.DRAFT
    );
  }, [allAuctions]);

  const shouldCreateAuctionInBackend = useCallback(() => {
    if (!createAuction?.vehicle) return false;

    const stepsMap = {
      [VehicleType.CAR]: [
        AuctionItemState.STEP_BASIC_INFORMATION,
        AuctionItemState.STEP_VEHICLE_CONDITION,
        AuctionItemState.STEP_DAMAGES,
        AuctionItemState.STEP_ACCESSORIES,
        AuctionItemState.STEP_MEDIA,
        AuctionItemState.STEP_FREE_DESCRIPTION
      ],
      [VehicleType.MOTO]: [
        AuctionItemState.MOTO_STEP_BASIC_INFORMATION,
        AuctionItemState.MOTO_STEP_VEHICLE_CONDITION,
        AuctionItemState.MOTO_STEP_ACCESSORIES,
        AuctionItemState.MOTO_STEP_MEDIA,
        AuctionItemState.MOTO_STEP_FREE_DESCRIPTION
      ]
    };

    return stepsMap[createAuction.vehicle.vehicleType]?.includes(
      createAuction.itemState as AuctionItemState
    );
  }, [createAuction]);

  const createAuctionInBackend = useCallback(async () => {
    const isValidPath =
      location.pathname === "/" || location.pathname === "/moto";
    // Early return if conditions aren't met
    // User can only have ONE draft at a time, so check for ANY draft
    if (
      !isAuthenticated ||
      !shouldCreateAuctionInBackend() ||
      anyDraftAuction ||
      isLoadingUserAuctions ||
      processingAuctionRef.current ||
      !isValidPath
    ) {
      return false;
    }

    processingAuctionRef.current = true;
    setProcessingAuction(true);

    try {
      await api.url("/auctions").post(createAuction).json();
      await queryClient.invalidateQueries({
        queryKey: [ConsumerQueryKeys.AUCTIONS + "/draft"]
      });
      setCreateAuction({});
      return true;
    } catch (error) {
      console.error("Failed to create auction:", error);
      setCreateAuction({});
      return false;
    } finally {
      processingAuctionRef.current = false;
      setProcessingAuction(false);
    }
  }, [
    isAuthenticated,
    shouldCreateAuctionInBackend,
    anyDraftAuction,
    isLoadingUserAuctions,
    location.pathname,
    createAuction,
    queryClient,
    setCreateAuction
  ]);

  return { createAuctionInBackend, processingAuction };
};
